import { useEffect, useState } from "react";
import { AppLayout } from "@/components/layout/AppLayout";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TID } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, ShieldOff, Loader2, CheckCircle2, KeyRound, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { NotificationPreferences } from "@/components/settings/NotificationPreferences";

export default function SecurityPage() {
  const { user, refresh } = useAuth();
  const [step, setStep] = useState("idle"); // idle | qr | done
  const [setupData, setSetupData] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  // Change-password state
  const [pwCurrent, setPwCurrent] = useState("");
  const [pwNew, setPwNew] = useState("");
  const [pwConfirm, setPwConfirm] = useState("");
  const [pwBusy, setPwBusy] = useState(false);

  useEffect(() => { setStep(user?.totp_enabled ? "done" : "idle"); }, [user]);

  // Deep-link from email footers: /security?tab=notifications scrolls to the section
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("tab") === "notifications") {
      const t = setTimeout(() => document.getElementById("notifications")?.scrollIntoView({ behavior: "smooth" }), 400);
      return () => clearTimeout(t);
    }
  }, []);

  const start = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/setup");
      setSetupData(data);
      setStep("qr");
    } catch { toast.error("Could not start 2FA setup"); }
    finally { setBusy(false); }
  };

  const enable = async () => {
    if (code.length < 6) return;
    setBusy(true);
    try {
      await api.post("/auth/2fa/enable", { code });
      await refresh();
      setStep("done");
      setCode("");
      toast.success("Two-factor authentication enabled");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invalid code");
    } finally { setBusy(false); }
  };


  const changePassword = async (e) => {
    e.preventDefault();
    if (pwNew !== pwConfirm) {
      toast.error("New password and confirmation don't match");
      return;
    }
    setPwBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: pwCurrent, new_password: pwNew });
      toast.success("Password updated");
      setPwCurrent(""); setPwNew(""); setPwConfirm("");
      await refresh();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (Array.isArray(d) ? d[0]?.msg : "Password change failed"));
    } finally { setPwBusy(false); }
  };

  return (
    <AppLayout title="Security" subtitle="Password and two-factor authentication for your account">
      <div className="max-w-2xl">
        {user?.must_change_password && (
          <div data-testid="security-must-change-banner" className="mb-4 flex items-start gap-3 p-4 rounded-lg border border-amber-200 bg-amber-50">
            <AlertTriangle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
            <div className="text-sm">
              <div className="font-medium text-amber-900">Password change required</div>
              <div className="text-amber-800">Your account uses a temporary password. Please set a new password below before continuing to use the platform.</div>
            </div>
          </div>
        )}
        {user?.must_enable_2fa && !user?.must_change_password && (
          <div data-testid="security-must-2fa-banner" className="mb-4 flex items-start gap-3 p-4 rounded-lg border border-rose-200 bg-rose-50">
            <AlertTriangle className="w-5 h-5 text-rose-700 shrink-0 mt-0.5" />
            <div className="text-sm">
              <div className="font-medium text-rose-900">Two-factor authentication required</div>
              <div className="text-rose-800">GBE Hub requires an authenticator app for all users. Please enable 2FA below to continue using the platform.</div>
            </div>
          </div>
        )}
        <div className="bg-white border border-slate-200 rounded-lg p-6">
          <div className="flex items-start gap-4">
            <div className={`w-11 h-11 rounded-md flex items-center justify-center ${user?.totp_enabled ? "bg-emerald-50 border border-emerald-200" : "bg-slate-50 border border-slate-200"}`}>
              {user?.totp_enabled ? <ShieldCheck className="w-5 h-5 text-emerald-700" /> : <ShieldOff className="w-5 h-5 text-slate-500" />}
            </div>
            <div className="flex-1">
              <h3 className="font-display text-lg font-semibold text-slate-900">Authenticator App</h3>
              <p className="text-sm text-slate-500">
                Use Google Authenticator, Microsoft Authenticator or 1Password to generate a 6-digit code every time you sign in.
              </p>
              <div className="mt-3">
                {user?.totp_enabled ? (
                  <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700 font-medium">
                    <CheckCircle2 className="w-3 h-3" /> Enabled
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border border-slate-200 bg-slate-50 text-slate-600 font-medium">
                    Not enabled
                  </span>
                )}
              </div>
            </div>
            <div>
              {step === "done" ? (
                <span data-testid="security-2fa-mandatory-note" className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border border-slate-200 bg-slate-50 text-slate-500 font-medium">
                  Required for all accounts
                </span>
              ) : step === "idle" ? (
                <Button data-testid={TID.security2FAEnable} onClick={start} disabled={busy} className="bg-gbe hover:bg-gbe-hover text-white">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enable 2FA"}
                </Button>
              ) : null}
            </div>
          </div>

          {step === "qr" && setupData && (
            <div className="mt-6 border-t border-slate-100 pt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-slate-900">1. Scan with your Authenticator</h4>
                <p className="text-xs text-slate-500 mb-3">Open Google/Microsoft Authenticator and tap "+" → "Scan a QR code".</p>
                <div className="inline-block p-3 border border-slate-200 rounded-md bg-white">
                  <img src={setupData.qr_data_uri} alt="Authenticator QR" className="w-44 h-44" />
                </div>
                <div className="mt-3 text-[11px] text-slate-500">
                  Or enter this secret manually:
                  <div className="mt-1 font-mono text-xs bg-slate-50 border border-slate-200 rounded px-2 py-1 select-all break-all">
                    {setupData.secret}
                  </div>
                </div>
              </div>
              <div>
                <h4 className="font-medium text-slate-900">2. Enter the 6-digit code</h4>
                <p className="text-xs text-slate-500 mb-3">Type the code shown in your Authenticator app to confirm setup.</p>
                <Input
                  data-testid={TID.security2FAVerifyInput}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="123 456"
                  className="font-mono text-center text-lg tracking-[0.4em] h-11"
                />
                <Button
                  data-testid={TID.security2FAVerify}
                  onClick={enable}
                  disabled={busy || code.length < 6}
                  className="mt-3 w-full bg-gbe hover:bg-gbe-hover text-white"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verify & enable"}
                </Button>
                <button
                  onClick={() => { setStep("idle"); setSetupData(null); setCode(""); }}
                  className="mt-2 w-full text-xs text-slate-500 hover:text-slate-800"
                >
                  Cancel setup
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="mt-6 bg-white border border-slate-200 rounded-lg p-6">
          <div className="flex items-start gap-4">
            <div className="w-11 h-11 rounded-md bg-slate-50 border border-slate-200 flex items-center justify-center">
              <KeyRound className="w-5 h-5 text-slate-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-display text-lg font-semibold text-slate-900">Change password</h3>
              <p className="text-sm text-slate-500">Minimum 8 characters, must contain at least one letter and one digit.</p>
            </div>
          </div>
          <form onSubmit={changePassword} className="mt-5 space-y-3">
            <div>
              <Label htmlFor="pw-current" className="text-slate-700 text-xs">Current password</Label>
              <Input
                id="pw-current"
                data-testid="security-pw-current"
                type="password"
                autoComplete="current-password"
                value={pwCurrent}
                onChange={(e) => setPwCurrent(e.target.value)}
                required
                className="mt-1.5"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="pw-new" className="text-slate-700 text-xs">New password</Label>
                <Input
                  id="pw-new"
                  data-testid="security-pw-new"
                  type="password"
                  autoComplete="new-password"
                  value={pwNew}
                  onChange={(e) => setPwNew(e.target.value)}
                  required
                  minLength={8}
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label htmlFor="pw-confirm" className="text-slate-700 text-xs">Confirm new password</Label>
                <Input
                  id="pw-confirm"
                  data-testid="security-pw-confirm"
                  type="password"
                  autoComplete="new-password"
                  value={pwConfirm}
                  onChange={(e) => setPwConfirm(e.target.value)}
                  required
                  minLength={8}
                  className="mt-1.5"
                />
              </div>
            </div>
            <div className="pt-2 flex justify-end">
              <Button
                type="submit"
                data-testid="security-pw-submit"
                disabled={pwBusy || !pwCurrent || pwNew.length < 8 || pwNew !== pwConfirm}
                className="bg-gbe hover:bg-gbe-hover text-white"
              >
                {pwBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update password"}
              </Button>
            </div>
          </form>
        </div>

        <div className="mt-6 bg-white border border-slate-200 rounded-lg p-6 text-sm text-slate-600">
          <h3 className="font-display font-semibold text-slate-900 mb-1">Account</h3>
          <div className="grid grid-cols-2 gap-4 mt-2 text-sm">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-slate-500">Name</div>
              <div className="text-slate-900">{user?.name}</div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-slate-500">Email</div>
              <div className="text-slate-900">{user?.email}</div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-slate-500">Role</div>
              <div className="text-slate-900 capitalize">{user?.role}</div>
            </div>
          </div>
        </div>

        <div className="mt-6">
          <NotificationPreferences />
        </div>
      </div>
    </AppLayout>
  );
}
